/**
 * MarinaAdvancedSettings - Cancellation Policies, Revenue Forecast,
 * Expiry Alerts, Webhooks for the Marina module.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Chip, IconButton, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
  Divider,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon   from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import WarningIcon from "@mui/icons-material/Warning";
import api from "../../utils/api";

const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

// ── Revenue Forecast ───────────────────────────────────────────────
const RevenueForecast = ({ marinaId }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [months,  setMonths]  = useState(3);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/marina-revenue-forecast/${marinaId}?months=${months}`); setData(r.data); }
    catch {} finally { setLoading(false); }
  }, [marinaId, months]);

  useEffect(() => { if (marinaId) load(); }, [marinaId, months, load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const res = await fetch(`${window.location.origin}/api/marina-reservations-export/${marinaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download=`marina-reservations-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setExporting(false); }
  };

  return (
    <Box>
      <Box sx={{ display:"flex", gap:2, mb:2.5, flexWrap:"wrap", alignItems:"center" }}>
        <TextField select size="small" label="Forecast Months" value={months} onChange={e=>setMonths(Number(e.target.value))} sx={{ ...fieldSx, minWidth:160 }}>
          {[1,2,3,6,12].map(m=><MenuItem key={m} value={m}>{m} month{m>1?"s":""}</MenuItem>)}
        </TextField>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }}>
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </Box>
      {loading ? <CircularProgress size={24} /> : data && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Expected Revenue by Month</Typography>
          <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden", mb:2.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
                <TableRow>{["Month","Reservations","Expected Revenue","Collected","Slips Booked"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {data.future_revenue.map(r=>(
                  <TableRow key={r.month} hover>
                    <TableCell sx={{ fontWeight:600, color:"#223142" }}>{r.month}</TableCell>
                    <TableCell sx={{ fontSize:12 }}>{r.reservations}</TableCell>
                    <TableCell sx={{ fontSize:12, fontWeight:700, color:"#16a34a" }}>${Number(r.expected_revenue||0).toLocaleString("en-US",{minimumFractionDigits:2})}</TableCell>
                    <TableCell sx={{ fontSize:12 }}>${Number(r.collected||0).toLocaleString("en-US",{minimumFractionDigits:2})}</TableCell>
                    <TableCell sx={{ fontSize:12 }}>{r.slips_booked}</TableCell>
                  </TableRow>
                ))}
                {!data.future_revenue.length && <TableRow><TableCell colSpan={5} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No upcoming reservations in this period.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

// ── Expiry Alerts ──────────────────────────────────────────────────
const ExpiryAlerts = ({ marinaId }) => {
  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [days,    setDays]    = useState(60);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/marina-expiry-alerts/${marinaId}?days=${days}`); setAlerts(r.data||[]); }
    catch {} finally { setLoading(false); }
  }, [marinaId, days]);

  useEffect(() => { if (marinaId) load(); }, [marinaId, days, load]);

  const fmtDate = d => d ? new Date(d.slice?.(0,10)+'T00:00:00').toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "N/A";
  const daysLeft = (d) => { if (!d) return null; const diff = Math.ceil((new Date(d.slice(0,10)) - new Date()) / 86400000); return diff; };

  return (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:2.5 }}>
        <TextField select size="small" label="Alert window" value={days} onChange={e=>setDays(Number(e.target.value))} sx={{ ...fieldSx, minWidth:160 }}>
          {[30,60,90,180].map(d=><MenuItem key={d} value={d}>Within {d} days</MenuItem>)}
        </TextField>
        {alerts.length > 0 && <Chip icon={<WarningIcon sx={{fontSize:14}} />} label={`${alerts.length} vessel${alerts.length!==1?"s":""} need attention`} sx={{ fontWeight:700, bgcolor:"rgba(220,38,38,0.1)", color:"#dc2626", borderRadius:"8px" }} />}
      </Box>
      {loading ? <CircularProgress size={24} /> : alerts.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius:2 }}>All active vessels have valid insurance and registration within {days} days.</Alert>
      ) : (
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(220,38,38,0.04)" }}>
              <TableRow>{["Vessel","Owner","Insurance Expires","Reg. Expires","Action"].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {alerts.map(a=>{
                const insDays = daysLeft(a.insurance_expiry);
                const regDays = daysLeft(a.registration_expiry);
                return (
                  <TableRow key={a.vessel_id} hover>
                    <TableCell sx={{ fontWeight:600, color:"#223142" }}>{a.vessel_name}</TableCell>
                    <TableCell sx={{ fontSize:12 }}>{a.owner_name}</TableCell>
                    <TableCell>
                      {a.insurance_expiry ? (
                        <Chip size="small" label={`${fmtDate(a.insurance_expiry)} (${insDays!=null?(insDays<0?"EXPIRED":insDays+"d"):"N/A"})`}
                          sx={{ fontWeight:700, fontSize:10, borderRadius:"8px", bgcolor: insDays!=null&&insDays<0?"rgba(220,38,38,0.15)":"rgba(180,83,9,0.1)", color: insDays!=null&&insDays<0?"#dc2626":"#b45309" }} />
                      ) : <Typography variant="caption" sx={{ color:"#9ca3af" }}>N/A</Typography>}
                    </TableCell>
                    <TableCell>
                      {a.registration_expiry ? (
                        <Chip size="small" label={`${fmtDate(a.registration_expiry)} (${regDays!=null?(regDays<0?"EXPIRED":regDays+"d"):"N/A"})`}
                          sx={{ fontWeight:700, fontSize:10, borderRadius:"8px", bgcolor: regDays!=null&&regDays<0?"rgba(220,38,38,0.15)":"rgba(180,83,9,0.1)", color: regDays!=null&&regDays<0?"#dc2626":"#b45309" }} />
                      ) : <Typography variant="caption" sx={{ color:"#9ca3af" }}>N/A</Typography>}
                    </TableCell>
                    <TableCell>
                      {a.owner_email && <Button size="small" sx={{ textTransform:"none", fontSize:11, color:"#2f63ad" }}>Send Reminder</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

// ── Cancellation Policies ──────────────────────────────────────────
const CancellationPolicies = ({ marinaId }) => {
  const [policies, setPolicies] = useState([]);
  const [form,     setForm]     = useState({ name:"", days_before:7, refund_pct:100, description:"" });
  const [snack,    setSnack]    = useState({ open:false, msg:"", sev:"success" });
  const load = useCallback(async () => { const r = await api.get(`/marina-cancellation-policies/${marinaId}`); setPolicies(r.data||[]); }, [marinaId]);
  useEffect(() => { if (marinaId) load(); }, [marinaId, load]);
  const handleAdd = async () => {
    if (!form.name || form.days_before==null || form.refund_pct==null) { setSnack({ open:true, msg:"All fields required.", sev:"error" }); return; }
    try { await api.post("/marina-cancellation-policies", { ...form, marina_id: marinaId }); setForm({ name:"", days_before:7, refund_pct:100, description:"" }); setSnack({ open:true, msg:"Policy added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
        Define refund tiers based on how far in advance the cancellation is made.
      </Typography>
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Add Policy Tier</Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Policy Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={2}><TextField type="number" fullWidth size="small" label="Days Before" value={form.days_before} onChange={e=>setForm(f=>({...f,days_before:e.target.value}))} sx={fieldSx} helperText="Cancel X+ days before" /></Grid>
          <Grid item xs={12} sm={2}><TextField type="number" fullWidth size="small" label="Refund %" value={form.refund_pct} onChange={e=>setForm(f=>({...f,refund_pct:e.target.value}))} sx={fieldSx} helperText="0-100" /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={1}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ ...btnSx, py:1 }}>Add</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["Policy","Cancel Before","Refund %","Description",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {policies.map(p=>(
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontWeight:600, color:"#223142" }}>{p.name}</TableCell>
                <TableCell><Chip size="small" label={`${p.days_before}+ days`} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} /></TableCell>
                <TableCell><Chip size="small" label={`${p.refund_pct}%`} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor: p.refund_pct>0?"rgba(34,197,94,0.1)":"rgba(220,38,38,0.1)", color: p.refund_pct>0?"#16a34a":"#dc2626" }} /></TableCell>
                <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{p.description||"N/A"}</TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/marina-cancellation-policies/${p.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!policies.length && <TableRow><TableCell colSpan={5} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No cancellation policies defined.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Webhooks ───────────────────────────────────────────────────────
const MarinaWebhooks = () => {
  const [hooks, setHooks] = useState([]);
  const [form,  setForm]  = useState({ url:"", events:"reservation_created", secret_key:"" });
  const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });
  const load = useCallback(async () => { const r = await api.get("/marina-webhooks"); setHooks(r.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const handleAdd = async () => {
    if (!form.url) { setSnack({ open:true, msg:"URL required.", sev:"error" }); return; }
    try { await api.post("/marina-webhooks", form); setForm({ url:"", events:"reservation_created", secret_key:"" }); setSnack({ open:true, msg:"Added.", sev:"success" }); load(); }
    catch { setSnack({ open:true, msg:"Failed.", sev:"error" }); }
  };
  return (
    <Box>
      <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>Send HTTP POST notifications to external systems on marina events.</Alert>
      <Paper elevation={0} sx={cardSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Endpoint URL" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Events" value={form.events} onChange={e=>setForm(f=>({...f,events:e.target.value}))} sx={fieldSx} helperText="reservation_created, check_in, check_out" /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Secret" value={form.secret_key} onChange={e=>setForm(f=>({...f,secret_key:e.target.value}))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={btnSx}>Add Webhook</Button></Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}><TableRow>{["URL","Events","Active",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {hooks.map(h=>(
              <TableRow key={h.id} hover>
                <TableCell sx={{ fontSize:12, maxWidth:280 }}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:260 }}>{h.url}</Typography></TableCell>
                <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{h.events}</TableCell>
                <TableCell><Chip size="small" label={h.is_active?"Active":"Off"} onClick={async()=>{ await api.patch(`/marina-webhooks/${h.id}`,{is_active:!h.is_active}); load(); }} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:h.is_active?"rgba(34,197,94,0.1)":"rgba(0,0,0,0.06)", color:h.is_active?"#16a34a":"#6b778c", cursor:"pointer" }} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={async()=>{ await api.delete(`/marina-webhooks/${h.id}`); load(); }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!hooks.length && <TableRow><TableCell colSpan={4} sx={{ textAlign:"center", py:3, color:"#6b778c" }}>No webhooks.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}><Alert severity={snack.sev}>{snack.msg}</Alert></Snackbar>
    </Box>
  );
};

// ── Main ───────────────────────────────────────────────────────────
const MarinaAdvancedSettings = ({ marinaId }) => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Marina Settings & Tools</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>Revenue forecasting, expiry alerts, cancellation policies, and webhooks.</Typography>
      </Box>
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:44, fontSize:13 } }}>
          <Tab label="Revenue Forecast" />
          <Tab label="Expiry Alerts" />
          <Tab label="Cancellation Policies" />
          <Tab label="Webhooks" />
        </Tabs>
      </Paper>
      {tab===0 && <RevenueForecast marinaId={marinaId} />}
      {tab===1 && <ExpiryAlerts marinaId={marinaId} />}
      {tab===2 && <CancellationPolicies marinaId={marinaId} />}
      {tab===3 && <MarinaWebhooks />}
    </Box>
  );
};

export default MarinaAdvancedSettings;
